import { Controller } from "@hotwired/stimulus"
import { phraseTier } from "../lib/common_words"

const PLATFORM_LABELS = { mastodon: "Mastodon", hackernews: "HN", bluesky: "Bluesky" }
const PROBE_PLATFORMS = ["mastodon", "bluesky", "hackernews"]

// Headline verdict keys off the dictionary tier (instant, offline), so it stays stable
// across the pending -> resolved transition. null tier -> "good".
const VERDICT = {
  very_common: {
    text: "text-sentiment-negative-deep", dot: "bg-sentiment-negative",
    label: "Everyday word",
    sub: "Said constantly about everything. Most matches won't be about you.",
  },
  common: {
    text: "text-sentiment-neutral-deep", dot: "bg-sentiment-neutral",
    label: "Fairly common",
    sub: "Expect some unrelated posts mixed in. A more specific phrase sharpens it.",
  },
  good: {
    text: "text-sentiment-positive-deep", dot: "bg-sentiment-positive",
    label: "Good signal",
    sub: "Specific enough that nearly every match will be about you.",
  },
}

// Live per-platform reach bucket -> dot color. rare = quiet (good), common = busy (bad).
const REACH = {
  rare: "bg-sentiment-positive",
  moderate: "bg-sentiment-neutral",
  common: "bg-sentiment-negative",
}

export default class extends Controller {
  static targets = ["textarea", "results"]
  static values = { url: String }

  #debounce = null
  #requestId = 0

  connect() {
    if (this.textareaTarget.value.trim()) this.schedule()
  }

  // Instant client-side verdict on every keystroke (zero round trip) with the platform
  // reach still loading, then a debounced call that fills in live per-platform counts.
  schedule() {
    this.#renderInstant()
    clearTimeout(this.#debounce)
    this.#debounce = setTimeout(() => this.#fetch(), 400)
  }

  #terms() {
    return this.textareaTarget.value
      .split(/[\n,]/)
      .map((t) => t.trim())
      .filter(Boolean)
      .filter((t, i, all) => all.findIndex((o) => o.toLowerCase() === t.toLowerCase()) === i)
  }

  #renderInstant() {
    const terms = this.#terms()
    if (terms.length === 0) {
      this.resultsTarget.replaceChildren()
      this.#emitWorstTier(null)
      return
    }
    const results = terms.map((term) => ({
      term,
      blocked: false,
      platforms: {},
      frequency_tier: phraseTier(term),
    }))
    this.#render(results, true)
  }

  async #fetch() {
    const value = this.textareaTarget.value
    if (!value.trim()) return

    const id = ++this.#requestId
    const csrf = document.querySelector("meta[name=csrf-token]")?.content

    let response
    try {
      response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrf,
          Accept: "application/json",
        },
        body: new URLSearchParams({ terms_input: value }),
      })
    } catch {
      return
    }

    if (id !== this.#requestId || !response.ok) return

    const data = await response.json()
    if (id !== this.#requestId) return

    this.#render(data.results || [], false)
  }

  #render(results, pending) {
    this.resultsTarget.replaceChildren()
    results.forEach((result) => this.resultsTarget.appendChild(this.#card(result, pending)))
    this.#emitWorstTier(this.#worstTier(results))
  }

  // Red beats amber beats green: the submit guard only trips on a red (very_common) term.
  #worstTier(results) {
    if (results.some((r) => !r.blocked && r.frequency_tier === "very_common")) return "very_common"
    if (results.some((r) => !r.blocked && r.frequency_tier === "common")) return "common"
    return null
  }

  #emitWorstTier(tier) {
    this.dispatch("verdict", { detail: { tier } })
  }

  #card(result, pending) {
    const card = document.createElement("div")
    card.className = "rounded-lg border border-line bg-white p-4"

    const top = document.createElement("div")
    top.className = "flex items-center justify-between gap-3"

    const term = document.createElement("span")
    term.className = "font-mono text-[16px] font-semibold text-ink truncate"
    term.textContent = result.term
    top.appendChild(term)
    top.appendChild(pending ? this.#checking() : this.#verdict(result))
    card.appendChild(top)

    if (result.blocked) {
      const reason = document.createElement("p")
      reason.className = "mt-1.5 text-meta text-sentiment-negative-deep"
      reason.textContent = result.reason
      card.appendChild(reason)
      return card
    }

    const copy = VERDICT[result.frequency_tier] || VERDICT.good
    const sub = document.createElement("p")
    sub.className = "mt-1.5 text-meta text-ink-slate"
    sub.textContent = copy.sub
    card.appendChild(sub)

    card.appendChild(this.#platforms(result, pending))
    return card
  }

  #verdict(result) {
    const copy = VERDICT[result.frequency_tier] || VERDICT.good
    const span = document.createElement("span")
    span.className = `inline-flex items-center gap-1.5 text-[14.5px] font-semibold shrink-0 ${copy.text}`
    const dot = document.createElement("span")
    dot.className = `w-2 h-2 rounded-full ${copy.dot}`
    span.append(dot, document.createTextNode(copy.label))
    return span
  }

  #checking() {
    const span = document.createElement("span")
    span.className = "inline-flex items-center gap-2 text-[14.5px] font-semibold text-ink-slate shrink-0"
    span.append(this.#spinner(), document.createTextNode("Checking…"))
    return span
  }

  #platforms(result, pending) {
    const wrap = document.createElement("div")
    wrap.className = "mt-3 flex flex-wrap gap-2"
    if (pending) {
      PROBE_PLATFORMS.forEach((p) => wrap.appendChild(this.#skeletonChip(PLATFORM_LABELS[p])))
    } else {
      Object.entries(result.platforms || {}).forEach(([platform, info]) => {
        wrap.appendChild(this.#chip(platform, info))
      })
    }
    return wrap
  }

  #chip(platform, info) {
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 rounded-full bg-well px-2.5 py-1 text-[14px] font-semibold text-ink"
    const label = PLATFORM_LABELS[platform] || platform

    if (!info.available) {
      const dot = document.createElement("span")
      dot.className = "w-[7px] h-[7px] rounded-full bg-line-strong"
      chip.append(dot, document.createTextNode(`${label}: unavailable`))
      return chip
    }

    const dot = document.createElement("span")
    dot.className = `w-[7px] h-[7px] rounded-full ${REACH[info.bucket] || "bg-line-strong"}`
    const text = platform === "hackernews" && info.count != null
      ? `${label} ${info.count} · ${info.bucket}`
      : `${label}: ${info.bucket}`
    chip.append(dot, document.createTextNode(text))
    return chip
  }

  #skeletonChip(label) {
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-2 rounded-full bg-foot px-2.5 py-1 text-[14px] font-semibold text-ink-slate"
    chip.append(this.#spinner("w-3 h-3"), document.createTextNode(label))
    return chip
  }

  #spinner(size = "w-3.5 h-3.5") {
    const s = document.createElement("span")
    s.className = `inline-block ${size} border-2 border-brand border-t-transparent rounded-full animate-spin`
    return s
  }
}
